import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import { UserContext } from '../../../context/authContext';
import { DocumentContext } from '../../../context/documentContext';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

import { ZoomIn } from 'lucide-react';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { Carousel } from 'react-responsive-carousel';
import {
  citeOutline,
  documentTableItem,
  ISynopsisObject,
  sentenceItem
} from '../../../lib/models/interfaces';
import { useAlert } from '../../../context/alertContext';
import {
  calculateOutline,
  mergeOverlappingRegions
} from '../../../helpers/creeateSentencePaths';
import { DocumentSynopsis } from './Synopsis';
import { KoruLoading } from '../shared/KoruLoading';
import { BreadcrumbsPills } from '../shared/backAndHomeButtons';
import './documentOnlyViewer.css';
import { recordRumEvent } from '../../../hooks/rum';
import { ScrollDownArrow } from '../shared/scrollDownArrow';
import logger from '../../../helpers/Logger';
import { HighlightOverlay } from './highlightOverlay';
import { motion } from 'framer-motion';
import { ChatWithSummaryNewSub } from '../Chat/chatWithSummaryNewSub';
import { DocumentComments } from './comments';
import { PIIViewer } from './PIIDisplay';

// pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFProps {
  url?: string;
}

const SimplePDFViewer: React.FC<PDFProps> = ({ url }) => {
  const { auth, token, user } = useContext(UserContext);
  const { setCurrentDocId, allDocuments } = useContext(DocumentContext);
  const { setAlert } = useAlert();

  // refs
  const containerRef = useRef<HTMLDivElement>(null);

  const [numPages, setNumPages] = useState<{ [key: number]: number }>({});
  const [currentPage, setCurrentPage] = useState<{ [key: number]: number }>({});
  const [showDoc, setShowDoc] = useState<boolean>(false);
  const [presignedURLS, setPreSignedURLS] = useState<string[]>([]);
  const [queryParameters] = useSearchParams();
  // const [documentIds, setDocumentIds] = useState<string[]>([]);
  const [currentDocuments, setCurrentDocuments] = useState<documentTableItem[]>(
    []
  );
  const [carouselIndex, setCarouselIndex] = useState<number>(0);
  const [docsLoaded, setDocsLoaded] = useState<{ [key: number]: boolean }>({});
  const [pagesRendered, setPagesRendered] = useState<{
    [key: number]: boolean;
  }>({});
  const [docScales, setDocScales] = useState<{ [key: number]: number }>({});
  const [isZooming, setIsZooming] = useState<{ [key: number]: boolean }>({});
  const [citeOutline, setCiteOutline] = useState<citeOutline[]>([]);
  // Store per-document, per-page viewport dimensions
  const [pageViewports, setPageViewports] = useState<{
    [docIndex: number]: {
      [pageNumber: number]: { width: number; height: number };
    };
  }>({});
  const [showSynopsis, setShowSynopsis] = useState<boolean>(false);
  const [synopsisLoading, setSynopsisLoading] = useState<boolean>(false);
  const [synopsisObject, setSynopsisObject] = useState<ISynopsisObject>(
    {} as ISynopsisObject
  );
  const [sentenceMap, setSentenceMap] = useState<{
    [key: string]: sentenceItem;
  }>({});
  const [synopsisBuilt, setSynopsisBuilt] = useState<boolean>(false);
  const [allSentences, setAllSentences] = useState<sentenceItem[]>([]);

  // right hand side tabbed view.
  const [tabs, setTabs] = useState<string[]>([
    'AI Findings',
    'Synopsis',
    'Comments'
  ]);
  const [active, setActive] = useState('AI Findings');

  function onDocumentLoadSuccess({
    numPages,
    index
  }: {
    numPages: number;
    index: number;
  }) {
    setNumPages((prev) => ({ ...prev, [index]: numPages }));
    setCurrentPage((prev) => ({ ...prev, [index]: 1 }));
    setDocsLoaded((prev) => ({ ...prev, [index]: true }));
    setDocScales((prev) => ({ ...prev, [index]: 1.0 }));
  }

  const allDocumentsLoaded = useMemo(() => {
    return (
      presignedURLS.length > 0 &&
      presignedURLS.every((_, idx) => docsLoaded[idx] && pagesRendered[idx])
    );
  }, [presignedURLS, docsLoaded, pagesRendered]);

  useEffect(() => {
    if (allDocumentsLoaded) {
      setShowDoc(true);
      const piiInDocs = currentDocuments.some(
        (doc) => doc?.entitiesDetected!.length > 0
      );
      if (piiInDocs) {
        setTabs((prev) => [...prev, 'Sensitive Data']);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDocumentsLoaded]);

  const goToPrevPage = (index: number) => {
    const pageNUmberCurrent = currentPage[index];
    const numToUse = pageNUmberCurrent - 1 >= 1 ? pageNUmberCurrent - 1 : 1;
    setCurrentPage((prev) => ({
      ...prev,
      [index]: numToUse
    }));
  };

  const goToNextPage = (index: number) => {
    const pageNUmberCurrent = currentPage[index];
    const nextPage =
      pageNUmberCurrent + 1 <= numPages[index]
        ? pageNUmberCurrent + 1
        : numPages[index];
    setCurrentPage((prevPage) => ({
      ...prevPage,
      [index]: nextPage
    }));
  };

  const goToSpecificPage = (pageNo: number, index: number) => {
    if (index !== carouselIndex) {
      setCarouselIndex(index);
    }
    if (numPages[index] && pageNo >= 1 && pageNo <= numPages[index]) {
      setCurrentPage((prev) => ({ ...prev, [index]: pageNo }));
    }
  };

  const getALlIds = () => {
    const ids = queryParameters.getAll('documentId');
    const tempDocuments = [];
    for (const id of ids) {
      const thisDoc = allDocuments.find((ele) => ele._id === id);
      if (thisDoc) {
        tempDocuments.push(thisDoc);
      }
    }
    // setDocumentIds(ids);
    setCurrentDocuments(tempDocuments);
  };
  // Helper to fetch a PDF from URL and return as base64 string
  const fetchPDFAsBase64 = async (url: string): Promise<string> => {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
    });
  };

  const getImageFromS3 = async () => {
    if (!token) return;
    try {
      const ids = queryParameters.getAll('documentId');
      const base64Files = await Promise.all(
        ids.map(async (docId) => {
          const res = await axios.post(
            `${process.env.REACT_APP_KORU_API}/get-presigned-url?type=getDocument`,
            { documentId: docId },
            auth
          );
          const url = res.data.url;
          return await fetchPDFAsBase64(url);
        })
      );
      setPreSignedURLS(base64Files);
      setShowDoc(true);
    } catch (e) {
      console.log('Error from get Image', e);
      setShowDoc(false);
    }
  };

  useEffect(() => {
    if (queryParameters.get('documentId')) {
      setCurrentDocId(queryParameters.get('documentId')!);
    }
    getImageFromS3();
    getALlIds();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    getImageFromS3();
    getALlIds();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => {
    getALlIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDocuments]);

  useEffect(() => {
    if (queryParameters.get('documentId')) {
      setCurrentDocId(queryParameters.get('documentId')!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParameters]);
  console.log('docs loaded', docsLoaded);

  // Retrigger citeOutline rerender on docScales change (zoom level)
  useEffect(() => {
    if (citeOutline.length > 0) {
      console.log('rerendering outlines >>>>>>>>>>>>>>>>');
      // Reset citeOutline to trigger re-render
      const tempOutlines = citeOutline;
      setCiteOutline((prev) => []);

      setCiteOutline((prev) => [...tempOutlines]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docScales]);

  //TODO this is the list of sentence ParentTypes to Ignore
  const ignoreList = ['LAYOUT_SECTION_HEADER', 'LAYOUT_TITLE'];

  const buildSynopsis = async () => {
    if (!token) return;
    recordRumEvent('build_synopsis', {
      userSub: user.sub,
      numberOfDocs: currentDocuments.length
    });
    try {
      setSynopsisLoading(true);
      const allSentences = [];
      for (const document of currentDocuments) {
        if (document.chunks.length > 0) {
          for (let i = 0; i < document.chunks.length; i++) {
            const senResult = await axios.post(
              `${process.env.REACT_APP_KORU_API}/sentence?type=all`,
              { documentId: document._id, chunkNo: i },
              auth
            );
            console.log('sen result', senResult);
            const newSentences = senResult.data
              .result as unknown as sentenceItem[];
            console.log('new sentences', newSentences);
            allSentences.push(...newSentences);
          }
        }
        setAllSentences(allSentences);
      }
      const tempSynopsisObject: ISynopsisObject = {
        sentences: {},
        domains: {},
        obligations: {}
      };
      const tempSentenceObject = allSentences.reduce(
        (acc, curr) => {
          if (ignoreList.includes(curr.Parent_Type)) {
            return acc;
          }
          if (!acc[curr._id]) {
            acc[curr._id] = curr;
          }
          const seenLabel = new Set<string>();
          curr.activities?.forEach((ele) => {
            if (!seenLabel.has(ele.label)) {
              if (ele.obligation) {
                if (!tempSynopsisObject['obligations'][ele.label]) {
                  tempSynopsisObject['obligations'][ele.label] = [];
                }
                tempSynopsisObject['obligations'][ele.label].push(curr);
              }
              if (!tempSynopsisObject['sentences'][ele.label]) {
                tempSynopsisObject['sentences'][ele.label] = [];
              }
              tempSynopsisObject['sentences'][ele.label].push(curr);
              seenLabel.add(ele.label);
            }
          });
          curr.domains?.forEach((domain) => {
            if (!tempSynopsisObject['domains'][domain.label]) {
              tempSynopsisObject['domains'][domain.label] = [];
            }
            tempSynopsisObject['domains'][domain.label].push(curr);
          });
          return acc;
        },
        {} as { [key: string]: sentenceItem }
      );

      console.log('temps sentence map', tempSentenceObject);
      setSynopsisObject(tempSynopsisObject);
      setSentenceMap(tempSentenceObject);
      setSynopsisBuilt(true);
      setSynopsisLoading(false);
      setShowSynopsis(true);
    } catch (e) {
      const error = e as Error;
      setAlert(error.message, 'error');
    }
  };
  //! This is the function that handle display when a sentences or page number is clicked from right hand panel.

  const handleSentenceClick = (id: string, type: string, label?: string) => {
    if (type === 'sentence') {
      const temp = sentenceMap[id];
      console.log('temp sentence in click ====', temp);
      const docIndex = currentDocuments?.findIndex(
        (ele) => ele._id === temp.documentId
      );
      const outlineTemp = calculateOutline(
        temp.outline,
        temp.PageNumber,
        docIndex
      );
      console.log('temp out line', outlineTemp);
      setCiteOutline([outlineTemp]);
      goToSpecificPage(temp.PageNumber, docIndex);
    }
    if (type === 'domain' && label) {
      setCiteOutline([]);
      const temp = allSentences.filter((ele) => ele._id === id);
      const docIndex = currentDocuments?.findIndex(
        (ele) => ele._id === temp[0].documentId
      );
      const relevant = allSentences.filter(
        (sentence) =>
          currentDocuments[docIndex]._id === temp[0].documentId &&
          sentence.PageNumber === temp[0].PageNumber
      );

      console.log('relevant sentences', relevant);

      const toHighlight: sentenceItem[] = [];
      relevant.forEach((sen) => {
        sen.domains?.forEach((domain) => {
          if (domain.label === label) {
            toHighlight.push(sen);
          }
        });
      });
      console.log('toHIghlight', toHighlight);
      const outlineTemp = [];
      for (const senToHighLight of toHighlight) {
        const oLine = calculateOutline(
          senToHighLight.outline,
          senToHighLight.PageNumber,
          docIndex
        );
        outlineTemp.push(oLine);
      }
      if (outlineTemp.length > 0) {
        const merged = mergeOverlappingRegions(outlineTemp);
        setCiteOutline(merged);
      } else {
        setCiteOutline(outlineTemp);
      }
      goToSpecificPage(temp[0].PageNumber, docIndex);
    }
  };
  useEffect(() => {
    if (showSynopsis && !synopsisBuilt) {
      buildSynopsis();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSynopsis]);

  useEffect(() => {
    console.log('doc scale changed');
    const tempOutline = citeOutline;
    setCiteOutline((prev) => []);
    setCiteOutline(tempOutline);
  }, [citeOutline, docScales]);

  const handleDocNavigation = (e: any) => {
    console.log('e in click handler', e);
  };

  const border = 'border border-black rounded-xl';

  let link = document.querySelector("link[rel~='icon']");
  logger.info('link', link);
  return (
    <>
      <div className="flex flex-col items-center h-[85vh]">
        <div className="flex w-full items-center p-4 grid-col ">
          {/* <BackAndHomeButtons
            showBackButton={true}
            currentFolder={null}
            projectId={null}
          /> */}
          <div className="w-full">
            <BreadcrumbsPills
              carouselIndex={carouselIndex}
              documents={currentDocuments}
            />
          </div>
          <div className="w-full text-center mb-2 text-lg font-semibold ">
            <div className="px-4 py-2 rounded-md w-full bg-[#56dac5] text-white ">
              {currentDocuments[carouselIndex]?.documentFileName}
            </div>
          </div>
        </div>
        <div className="flex flex-row justify-around h-full w-full">
          <div
            className={`w-[48%] ${border} overflow-y-scroll`}
            style={{ scrollbarWidth: 'none' }}
            ref={containerRef}
          >
            <Carousel
              // className="overflow-hidden"

              width={'100%'}
              showIndicators={false}
              showStatus={true}
              showArrows
              showThumbs={true}
              selectedItem={carouselIndex}
              onChange={(index) => setCarouselIndex(index)}
              renderThumbs={(children: React.ReactNode[]) =>
                (children as React.ReactElement[]).map((_, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f0f0f0',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 18,
                      fontWeight: 500
                    }}
                  >
                    {idx + 1}
                  </div>
                ))
              }
              renderArrowNext={(handleDocNavigation, hasNext, label = 'next') =>
                hasNext && (
                  <button
                    type="button"
                    onClick={handleDocNavigation}
                    title={label}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#56dac5]/50 rounded-full p-2 shadow"
                    style={{ zIndex: 10 }}
                    aria-label="Next document"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      stroke="#075e4fff" // 👈 Your green arrow
                      fill="#075e4fff"
                      strokeWidth="2"
                    >
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </button>
                )
              }
              renderArrowPrev={(
                handleDocNavigation,
                hasPrev,
                label = 'previous'
              ) =>
                hasPrev && (
                  <button
                    type="button"
                    onClick={handleDocNavigation}
                    title={label}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-[#56dac5]/50 rounded-full p-2 shadow"
                    style={{ zIndex: 10 }}
                    aria-label="Previous document"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      stroke="#075e4fff"
                      fill="#075e4fff"
                      strokeWidth="2"
                    >
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </button>
                )
              }
            >
              {presignedURLS.map((ele, index) => {
                return (
                  <div
                    key={currentDocuments[index]?._id ?? index}
                    className={`flex flex-col items-center space-y-4 w-full h-full p-2`}
                  >
                    {showDoc ? (
                      <div
                        className="w-[50vw] max-w-2xl h-[75vh] overflow-y-scroll"
                        style={{ scrollbarWidth: 'none' }}
                      >
                        <Document
                          file={ele}
                          onLoadSuccess={({ numPages }) =>
                            onDocumentLoadSuccess({ numPages, index })
                          }
                          loading={null}
                        >
                          <div className="relative w-full h-full">
                            <Page
                              key={`${index}-${currentPage[index]}-${docScales[index]}`}
                              pageNumber={currentPage[index]}
                              scale={docScales[index] || 1}
                              renderAnnotationLayer={false}
                              onRenderSuccess={async (page) => {
                                // Get viewport for this page and store it
                                // page is a PDFPageProxy
                                try {
                                  // pdfjs-dist types: getViewport({ scale })
                                  const viewport = page.getViewport({
                                    scale: docScales[index] || 1
                                  });
                                  console.log('view port in page', viewport);
                                  if (
                                    viewport.height < viewport.width &&
                                    docScales[index] === 1
                                  ) {
                                    console.log('setting zoom');
                                    setDocScales((prev) => ({
                                      ...prev,
                                      [index]: 0.85
                                    }));
                                  }
                                  setPageViewports((prev) => ({
                                    ...prev,
                                    [index]: {
                                      ...(prev[index] || {}),
                                      [currentPage[index]]: {
                                        width: viewport.width,
                                        height: viewport.height
                                      }
                                    }
                                  }));
                                } catch (e) {
                                  // fallback: do nothing
                                }
                                setPagesRendered((prev) => ({
                                  ...prev,
                                  [index]: true
                                }));
                              }}
                            />
                            <HighlightOverlay
                              outlines={citeOutline}
                              pageNumber={currentPage[index]}
                              docIndex={carouselIndex}
                              scale={docScales[index] || 1}
                              pageViewports={pageViewports}
                            />
                          </div>
                        </Document>
                      </div>
                    ) : (
                      <KoruLoading centerText="Loading Documents" />
                    )}
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => {
                          setCiteOutline([]);
                          goToPrevPage(index);
                        }}
                        disabled={currentPage[index] === 1}
                        className="px-4 py-1 bg-gray-200 rounded disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span>
                        Page {currentPage[index]} of {numPages[index]}
                      </span>
                      <button
                        onClick={() => {
                          setCiteOutline([]);
                          goToNextPage(index);
                        }}
                        disabled={numPages !== null && currentPage === numPages}
                        className="px-4 py-1 bg-gray-200 rounded disabled:opacity-50"
                      >
                        Next
                      </button>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          const page = Number(
                            (
                              e.currentTarget.elements.namedItem(
                                'page'
                              ) as HTMLInputElement
                            ).value
                          );
                          if (
                            page >= 1 &&
                            numPages[index] &&
                            page <= numPages[index]
                          ) {
                            setCurrentPage((prev) => ({
                              ...prev,
                              [index]: page
                            }));
                          }
                          setCiteOutline([]);
                        }}
                        className="flex items-center space-x-2"
                      >
                        <label htmlFor="page" className="text-sm">
                          Jump to page:
                        </label>
                        <input
                          type="number"
                          name="page"
                          min={1}
                          max={numPages[index] ?? 1}
                          defaultValue={currentPage[index]}
                          className="w-16 px-2 py-1 border rounded"
                        />
                        <button
                          type="submit"
                          className="px-3 py-1 bg-blue-200 rounded"
                        >
                          Go
                        </button>
                      </form>
                    </div>
                    <div className="flex items-center mb-2 w-full">
                      <div className="relative w-full">
                        <input
                          type="range"
                          min="0.25"
                          max="3"
                          step="0.05"
                          value={docScales[index] || 1.0}
                          onChange={(e) => {
                            const newScale = parseFloat(e.target.value);
                            setDocScales((prev) => ({
                              ...prev,
                              [index]: newScale
                            }));
                          }}
                          onMouseDown={() =>
                            setIsZooming((prev) => ({ ...prev, [index]: true }))
                          }
                          onMouseUp={() =>
                            setIsZooming((prev) => ({
                              ...prev,
                              [index]: false
                            }))
                          }
                          onTouchStart={() =>
                            setIsZooming((prev) => ({ ...prev, [index]: true }))
                          }
                          onTouchEnd={() =>
                            setIsZooming((prev) => ({
                              ...prev,
                              [index]: false
                            }))
                          }
                          className="w-full appearance-none h-2 bg-gray-200 rounded-full outline-none"
                        />
                        {isZooming[index] && (
                          <div className="absolute top-[-24px] left-1/2 transform -translate-x-1/2 text-sm text-gray-700">
                            {(docScales[index] || 1.0).toFixed(2)}x
                          </div>
                        )}
                      </div>
                      <ZoomIn className="mr-2 text-gray-700" />
                    </div>
                  </div>
                );
              })}
            </Carousel>
            {!allDocumentsLoaded && (
              <KoruLoading centerText="Loading Documents" />
            )}
            <ScrollDownArrow
              containerRef={containerRef as React.RefObject<HTMLElement>}
              bottomValue="7%"
            />
          </div>
          {/* right hand side of screen */}
          <div
            className={`${border} min-h-[100%] w-[48%] p-4 overflow-y-scroll`}
            style={{ scrollbarWidth: 'none' }}
          >
            <div className="w-full">
              <div className="flex space-x-4 border-b border-slate-300 pb-2">
                {tabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => {
                      if (tab === 'Synopsis') {
                        setShowSynopsis(true);
                      }
                      setActive(tab);
                    }}
                    className={`relative px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                      active === tab
                        ? 'text-teal-600'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {tab}
                    {active === tab && (
                      <motion.div
                        layoutId="underline"
                        className="absolute left-0 right-0 h-0.5 bg-teal-600 rounded-full -bottom-1"
                      />
                    )}
                  </button>
                ))}
              </div>

              <div
                className={`p-4 ${active === 'Synopsis' ? '' : 'bg-white'} min-h-[150px]`}
              >
                <div className={active === 'Synopsis' ? 'block' : 'hidden'}>
                  {/* <h2 className="text-lg font-semibold mb-2">Synopsis</h2> */}
                  {!synopsisLoading && (
                    <DocumentSynopsis
                      sentenceMap={sentenceMap}
                      synopsisObject={synopsisObject}
                      handleSentenceClick={handleSentenceClick}
                      currentDocuments={currentDocuments}
                      setShowSynopsis={setShowSynopsis}
                    />
                  )}
                  {synopsisLoading && (
                    <KoruLoading centerText="Building Synopsis" />
                  )}
                </div>

                <div className={active === 'AI Findings' ? 'block' : 'hidden'}>
                  <ChatWithSummaryNewSub
                    type={'sub'}
                    goToSpecificPage={goToSpecificPage}
                    currentDocuments={currentDocuments}
                    setOutline={setCiteOutline}
                  />
                </div>

                <div className={active === 'Comments' ? 'block' : 'hidden'}>
                  <DocumentComments
                    documentIds={currentDocuments}
                    carouselIndex={carouselIndex}
                  />
                </div>
                <div
                  className={active === 'Sensitive Data' ? 'block' : 'hidden'}
                >
                  <PIIViewer
                    documents={currentDocuments}
                    carouselIndex={carouselIndex}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* end right side */}
        </div>
      </div>
    </>
  );
};

export default SimplePDFViewer;
